import { useEffect, useRef, useState } from "react";
import { Seo } from "@/components/Seo";
import { Panel, Tag } from "@/components/System";
import { TERMINAL_ART } from "@/data/content";

const PROMPT = "agentvm@terminal:~$";

const WELCOME = [
    "AGENTVM://TERMINAL v0.1 — SIMULATION",
    "secure shell established. type 'help' for modules.",
    "",
];

const TerminalPage = () => {
    const [history, setHistory] = useState(WELCOME.map((t) => ({ type: "out", text: t })));
    const [input, setInput] = useState("");
    const scrollRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    }, [history]);

    const run = (raw) => {
        const cmd = raw.trim().toLowerCase();
        const entry = [{ type: "cmd", text: raw }];
        if (!cmd) {
            setHistory((h) => [...h, ...entry]);
            return;
        }
        if (cmd === "clear") {
            setHistory([]);
            return;
        }
        const out = TERMINAL_ART[cmd];
        if (out) {
            setHistory((h) => [...h, ...entry, ...out.map((t) => ({ type: "out", text: t })), { type: "out", text: "" }]);
        } else {
            setHistory((h) => [
                ...h,
                ...entry,
                { type: "err", text: `module not found: ${cmd}` },
                { type: "out", text: "type 'help' for available modules." },
                { type: "out", text: "" },
            ]);
        }
    };

    return (
        <div>
            <Seo path="/terminal" />
            <section className="mx-auto flex min-h-screen max-w-5xl flex-col px-4 pb-16 pt-28 sm:px-6">
                <div className="vm-micro mb-6 flex flex-wrap items-center gap-3">
                    AGENTVM://TERMINAL <Tag tone="concept">SIMULATION</Tag>
                </div>
                <h1 className="text-3xl font-bold tracking-tight text-[#F5F9FF] sm:text-5xl">
                    TALK TO THE <span className="text-[#00D9FF] vm-glow">SYSTEM.</span>
                </h1>
                <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#B8C7D9] sm:text-base">
                    A live command-line view of the AgentVM operating model. All output here is simulated interface
                    telemetry, clearly labeled as such.
                </p>

                <Panel title="AGENTVM://SHELL" right="TTY-01" className="scanlines mt-10 flex-1" testid="terminal-shell">
                    <div
                        ref={scrollRef}
                        onClick={() => inputRef.current?.focus()}
                        className="h-[52vh] cursor-text overflow-y-auto p-5 font-mono text-xs leading-6 sm:text-[13px]"
                        data-testid="terminal-output"
                        aria-live="polite"
                    >
                        {history.map((line, i) => (
                            <div
                                key={i}
                                className={
                                    line.type === "cmd"
                                        ? "text-[#F5F9FF]"
                                        : line.type === "err"
                                          ? "text-red-400"
                                          : "whitespace-pre-wrap text-[#B8C7D9]/85"
                                }
                            >
                                {line.type === "cmd" ? (
                                    <>
                                        <span className="text-[#00D9FF]">{PROMPT}</span> {line.text}
                                    </>
                                ) : (
                                    line.text || " "
                                )}
                            </div>
                        ))}
                        <div className="flex items-center">
                            <span className="mr-2 shrink-0 text-[#00D9FF]">{PROMPT}</span>
                            <input
                                ref={inputRef}
                                data-testid="terminal-input"
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        run(input);
                                        setInput("");
                                    }
                                }}
                                className="w-full bg-transparent font-mono text-xs text-[#F5F9FF] caret-[#00D9FF] outline-none sm:text-[13px]"
                                autoFocus
                                aria-label="Terminal command input"
                                spellCheck={false}
                                autoComplete="off"
                            />
                        </div>
                    </div>
                </Panel>

                <div className="mt-4 flex flex-wrap gap-2" data-testid="terminal-shortcuts">
                    {["help", "status", "identity", "runtime", "attestation", "evidence", "execution", "agents", "economy", "arc", "docs", "sources", "clear"].map((c) => (
                        <button
                            key={c}
                            data-testid={`terminal-cmd-${c}`}
                            onClick={() => run(c)}
                            className="border border-[rgba(0,217,255,0.2)] px-3 py-1 font-mono text-[10px] tracking-[0.18em] text-[#B8C7D9]/80 transition-colors hover:border-[#00D9FF]/60 hover:text-[#00D9FF]"
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>
        </div>
    );
};

export default TerminalPage;
